import React, { ElementType } from 'react';
import { TypographyBase } from '#components/typography/typography-base/typography-base.tsx';
import { HtmlToReact } from '#components/html-to-react/html-to-react.tsx';
import { startsWithBlockLevelTag } from '#components/html-to-react/html-to-react.utils.ts';
import { type GDSHTMLTagOptions } from '#components/html-to-react/html-to-react.config.ts';
import type { GDSTypography } from '../typography';
import {
  polymorphicForwardRef,
  type PolymorphicComponentPropWithRef,
  type PolymorphicRef,
} from '../../../types';
import type { GDSVariantProps } from '../../../theme';
import { styled } from '../../../theme';
import { GenesisCoreInspector } from '../../../test-utils/genesis-core-inspector';

export type GDSParagraphTextSize = 'base' | 'sm' | 'xs';

export type GDSParagraphTextProps<C extends React.ElementType> =
  PolymorphicComponentPropWithRef<
    C,
    {
      as?: ElementType;
      size?: GDSVariantProps<typeof StyledParagraphText>['size'];
      htmlStringOptions?: GDSHTMLTagOptions;
    }
  > &
    GDSTypography;

export type GDSParagraphTextComponent = <C extends React.ElementType = 'p'>(
  props: GDSParagraphTextProps<C>,
) => React.ReactElement | null;

const DISPLAY_NAME = 'ParagraphText';

const StyledParagraphText = styled(TypographyBase, {
  // preserve the line breaks provided to this component regardless of whether it is rendered as a <p> or <div>
  whiteSpace: 'pre-line',
  '> a:any-link': {
    textDecoration: 'underline',
  },
  variants: {
    size: {
      base: {
        typography: '$bodyOne',
        '&:not(:last-child)': {
          marginBlockEnd: '$oneAndHalf',
        },
      },
      sm: {
        typography: '$bodyTwo',
        '&:not(:last-child)': {
          marginBlockEnd: '$one',
        },
      },
      xs: {
        typography: '$caption',
        '&:not(:last-child)': {
          marginBlockEnd: '$quarter',
        },
      },
    },
  },
});

export const ParagraphText: GDSParagraphTextComponent = polymorphicForwardRef(
  <C extends React.ElementType = 'p'>(
    {
      children,
      className,
      css,
      emphasis = 'base',
      id,
      size = 'base',
      htmlStringOptions = {},
      as,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    }: GDSParagraphTextProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const beginsWithPOrDivTag =
      typeof children === 'string'
        ? startsWithBlockLevelTag(children)
        : undefined;
    // render the ParagraphText as a <div> if the content is a rich text string beginning with a <p> or <div> tags
    const derivedTag = as || (beginsWithPOrDivTag && 'div') || 'p';
    const isRichText = typeof children === 'string';
    const richTextOptions = {
      p: { component: ParagraphText, props: { emphasis, size } },
      ...htmlStringOptions,
    } satisfies GDSHTMLTagOptions;

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <StyledParagraphText
          as={derivedTag}
          className={['GDS-paragraph-text', className].join(' ')}
          css={css}
          emphasis={emphasis}
          id={id}
          ref={ref}
          size={size}
          data-testid={testId}
          {...props}
        >
          {isRichText ? (
            <HtmlToReact htmlString={children} options={richTextOptions} />
          ) : (
            children
          )}
        </StyledParagraphText>
      </GenesisCoreInspector>
    );
  },
);

// @ts-ignore
ParagraphText.displayName = DISPLAY_NAME;
